import React, { useState, useEffect } from 'react';
import { Save, Upload, Plus, Trash2, ExternalLink, Info, X, ChevronUp, ChevronDown, Eye, EyeOff, FileText, User, Link as LinkIcon, AlertCircle } from 'lucide-react';
import { databaseService, storageService, ID, PROFILE_IMAGES_BUCKET_ID } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';
import FontSelector from '../../../components/FontSelector/FontSelector';

export default function AboutMe() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentId, setDocumentId] = useState(null);

  const [formData, setFormData] = useState({
    title: 'about me',
    subtitle: 'Who am I?',
    name: 'PARVEJ HOSSAIN',
    nameFont: "'Playfair Display', serif",
    photoUrl: '',
    bioText: '',
    bioTextFont: "'Libre Baskerville', serif",
    researchLinks: []
  });

  const ABOUT_COLLECTION = 'about_me';

  useEffect(() => {
    loadAboutData();
  }, []);

  const loadAboutData = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(ABOUT_COLLECTION);

      if (result.success && result.data.documents.length > 0) {
        const data = result.data.documents[0];
        setDocumentId(data.$id);
        setFormData({
          title: data.title || 'about me',
          subtitle: data.subtitle || 'Who am I?',
          name: data.name || 'PARVEJ HOSSAIN',
          nameFont: data.nameFont || "'Playfair Display', serif",
          photoUrl: data.photoUrl || '',
          bioText: data.bioText || '',
          bioTextFont: data.bioTextFont || "'Libre Baskerville', serif",
          researchLinks: data.researchLinks ? JSON.parse(data.researchLinks) : []
        });
      }
    } catch (error) {
      console.error('Error loading about data:', error);
      showToast('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast('File size must be less than 5MB', 'error');
      return;
    }
    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file', 'error');
      return;
    }

    try {
      setUploading(true);
      const fileId = ID.unique();
      const result = await storageService.uploadFile(
        PROFILE_IMAGES_BUCKET_ID,
        fileId,
        file
      );

      if (result.success) {
        await storageService.setFilePublic(PROFILE_IMAGES_BUCKET_ID, result.data.$id);
        const fileUrl = storageService.getFileView(PROFILE_IMAGES_BUCKET_ID, result.data.$id);
        setFormData(prev => ({ ...prev, photoUrl: fileUrl }));
        showToast('Photo uploaded successfully', 'success');
      } else {
        showToast(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      showToast('Error uploading photo', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const dataToSave = {
        title: formData.title,
        subtitle: formData.subtitle,
        name: formData.name,
        nameFont: formData.nameFont,
        photoUrl: formData.photoUrl,
        bioText: formData.bioText,
        bioTextFont: formData.bioTextFont,
        researchLinks: JSON.stringify(formData.researchLinks)
      };

      let result;
      if (documentId) {
        result = await databaseService.updateDocument(ABOUT_COLLECTION, documentId, dataToSave);
      } else {
        result = await databaseService.createDocument(ABOUT_COLLECTION, dataToSave);
        if (result.success) setDocumentId(result.data.$id);
      }

      if (result.success) {
        showToast('About Me section saved successfully!', 'success');
      } else {
        showToast(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error saving about data:', error);
      showToast('Error saving data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddResearchLink = () => {
    setFormData({
      ...formData,
      researchLinks: [...formData.researchLinks, { name: '', url: '', iconUrl: '', order: formData.researchLinks.length, hidden: false }]
    });
  };

  const handleResearchIconUpload = async (index, file) => {
    if (!file) return;
    try {
      setUploading(true);
      const fileId = ID.unique();
      const result = await storageService.uploadFile(PROFILE_IMAGES_BUCKET_ID, fileId, file);
      if (result.success) {
        await storageService.setFilePublic(PROFILE_IMAGES_BUCKET_ID, result.data.$id);
        const iconUrl = storageService.getFileView(PROFILE_IMAGES_BUCKET_ID, result.data.$id);
        const newLinks = [...formData.researchLinks];
        newLinks[index].iconUrl = iconUrl;
        setFormData({ ...formData, researchLinks: newLinks });
      }
    } finally {
      setUploading(false);
    }
  };

  const updateLink = (index, field, value) => {
     const newLinks = [...formData.researchLinks];
     newLinks[index][field] = value;
     setFormData({ ...formData, researchLinks: newLinks });
  };

  const moveLink = (index, direction) => {
     const newLinks = [...formData.researchLinks];
     const newIndex = direction === 'up' ? index - 1 : index + 1;
     if (newIndex >= 0 && newIndex < newLinks.length) {
        [newLinks[index], newLinks[newIndex]] = [newLinks[newIndex], newLinks[index]];
        newLinks.forEach((l, i) => l.order = i);
        setFormData({ ...formData, researchLinks: newLinks });
     }
  };

  const deleteLink = (index) => {
     const newLinks = formData.researchLinks.filter((_, i) => i !== index);
     newLinks.forEach((l, i) => l.order = i);
     setFormData({ ...formData, researchLinks: newLinks });
  };

  if (loading && !formData.name) return (
     <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center gap-2">
           <div className="w-8 h-8 border-4 border-[#4C1D95] border-t-transparent rounded-full animate-spin" />
           <p className="text-[#4C1D95]">Loading...</p>
        </div>
     </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">About Me</h1>
          <p className="text-gray-500 mt-1">Manage the vintage-styled About Me section.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={loading || uploading}
          className="flex items-center gap-2 px-6 py-3 bg-[#4C1D95] text-white rounded-xl font-medium shadow-lg shadow-purple-900/20 hover:bg-[#3b1674] hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-5 h-5" />}
          <span>{loading ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Settings (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
           
           {/* Section 1: Identity */}
           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 relative group/card">
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#4C1D95] to-purple-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
              <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                 <div className="p-2 bg-purple-50 rounded-lg text-[#4C1D95]">
                    <User className="w-5 h-5" />
                 </div>
                 Identity & Headers
              </h2>
              
              <div className="space-y-5">
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                       <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Section Title</label>
                       <input
                          value={formData.title}
                          onChange={(e) => setFormData({...formData, title: e.target.value})}
                          className="w-full px-4 py-2 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#4C1D95] outline-none transition-all"
                       />
                    </div>
                    <div>
                       <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Subtitle</label>
                       <input
                          value={formData.subtitle}
                          onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                          className="w-full px-4 py-2 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#4C1D95] outline-none transition-all"
                       />
                    </div>
                 </div>

                 <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Vertical Name</label>
                    <input
                       value={formData.name}
                       onChange={(e) => setFormData({...formData, name: e.target.value.toUpperCase()})}
                       className="w-full px-4 py-2 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#4C1D95] outline-none transition-all uppercase tracking-widest font-bold"
                    />
                    <div className="mt-2">
                       <FontSelector value={formData.nameFont} onChange={(f) => setFormData({...formData, nameFont: f})} label="" compact previewText="Name Font" />
                    </div>
                 </div>
              </div>
           </div>

           {/* Section 2: Biography */}
           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 relative group/card">
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#4C1D95] to-purple-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
              <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                 <div className="p-2 bg-purple-50 rounded-lg text-[#4C1D95]">
                    <FileText className="w-5 h-5" />
                 </div>
                 Biography
              </h2>

              <div className="space-y-4">
                 <textarea
                    value={formData.bioText}
                    onChange={(e) => setFormData({...formData, bioText: e.target.value})}
                    rows={8}
                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#4C1D95] outline-none transition-all resize-none text-sm leading-relaxed"
                    placeholder="Write your story..."
                 />
                 <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{formData.bioText.length} characters</span>
                    <FontSelector value={formData.bioTextFont} onChange={(f) => setFormData({...formData, bioTextFont: f})} label="" compact previewText="Bio Font" />
                 </div>
              </div>
           </div>

           {/* Section 3: Research Links */}
           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 relative group/card leading-relaxed">
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#4C1D95] to-purple-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
              <div className="flex items-center justify-between mb-6">
                 <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <div className="p-2 bg-purple-50 rounded-lg text-[#4C1D95]">
                       <LinkIcon className="w-5 h-5" />
                    </div>
                    Research Links
                 </h2>
                 <button onClick={handleAddResearchLink} className="p-2 bg-gray-50 hover:bg-[#4C1D95] hover:text-white rounded-lg transition-colors">
                    <Plus className="w-4 h-4" />
                 </button>
              </div>

              <div className="space-y-3">
                 {formData.researchLinks.map((link, idx) => (
                    <div key={idx} className="p-3 bg-gray-50/80 rounded-xl border border-gray-200 flex gap-3 group/link">
                       <div className="relative w-12 h-12 bg-white rounded-lg border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden cursor-pointer hover:border-[#4C1D95] transition-colors">
                          {link.iconUrl ? (
                             <img src={link.iconUrl} alt="icon" className="w-full h-full object-cover" />
                          ) : (
                             <Upload className="w-5 h-5 text-gray-400" />
                          )}
                          <input type="file" onChange={(e) => handleResearchIconUpload(idx, e.target.files[0])} className="absolute inset-0 opacity-0 cursor-pointer" />
                       </div>
                       
                       <div className="flex-1 min-w-0 space-y-1.5">
                          <input
                             value={link.name}
                             onChange={(e) => updateLink(idx, 'name', e.target.value)}
                             placeholder="Platform Name"
                             className="w-full bg-transparent text-sm font-semibold text-gray-800 placeholder:text-gray-400 focus:outline-none"
                          />
                          <input
                             value={link.url}
                             onChange={(e) => updateLink(idx, 'url', e.target.value)}
                             placeholder="https://..."
                             className="w-full bg-transparent text-xs text-blue-600 placeholder:text-blue-300 focus:outline-none font-mono"
                          />
                       </div>

                       <div className="flex flex-col gap-1 opacity-50 group-hover/link:opacity-100 transition-opacity">
                          <button onClick={() => updateLink(idx, 'hidden', !link.hidden)} className={`p-1 rounded ${link.hidden ? 'text-gray-400' : 'text-green-600'}`}>{link.hidden ? <EyeOff className="w-3 h-3"/> : <Eye className="w-3 h-3"/>}</button>
                          <button onClick={() => deleteLink(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3"/></button>
                       </div>
                    </div>
                 ))}
                 {formData.researchLinks.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-sm bg-gray-50 rounded-xl border border-dashed border-gray-200">
                       No research links added yet.
                    </div>
                 )}
              </div>
           </div>

        </div>

        {/* Right Column: Live Preview (7 cols) */}
        <div className="lg:col-span-7 lg:sticky lg:top-8">
           <div className="bg-[#fcfbf9] rounded-3xl border border-gray-200 shadow-2xl overflow-hidden relative">
              {/* Vintage Texture Overlay */}
              <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
              
              <div className="bg-[#4C1D95]/5 border-b border-[#4C1D95]/10 px-4 py-3 flex items-center justify-between">
                 <div className="text-xs font-serif italic text-gray-500">The Portfolio Times</div>
                 <div className="text-xs font-bold text-[#4C1D95] uppercase tracking-widest">Vintage Edition</div>
              </div>

              <div className="p-8 lg:p-12 relative">
                 <div className="flex gap-8 relative">
                    {/* Vertical Name - Decorative */}
                    <div className="hidden lg:block absolute -right-4 top-0 h-full w-12 border-l-2 border-double border-gray-200 flex items-center justify-center">
                       <span className="transform rotate-90 whitespace-nowrap text-6xl text-gray-100 font-bold uppercase tracking-widest select-none origin-center" style={{ fontFamily: formData.nameFont }}>
                          {formData.name}
                       </span>
                    </div>

                    <div className="flex-1 space-y-12 pr-8">
                       {/* Header Part */}
                       <div className="flex flex-col gap-4 border-b-2 border-black pb-8">
                          <span className="text-sm font-bold uppercase tracking-[0.2em] text-gray-500">{formData.title}</span>
                          <h2 className="text-5xl font-serif text-gray-900 leading-tight italic">
                             {formData.subtitle}
                          </h2>
                       </div>

                       <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                          {/* Image Column */}
                          <div className="space-y-6">
                             <div className="relative aspect-[3/4] bg-gray-100 group overflow-hidden border-4 border-white shadow-xl shadow-gray-200 rotate-[-1deg] transition-transform hover:rotate-0">
                                {formData.photoUrl ? (
                                   <img src={formData.photoUrl} alt="Profile" className="w-full h-full object-cover grayscale contrast-125 sepia-[0.2]" />
                                ) : (
                                   <div className="flex items-center justify-center h-full text-gray-300">
                                      <User className="w-20 h-20" />
                                   </div>
                                )}
                                <div className="absolute inset-0 ring-1 ring-inset ring-black/10"></div>
                             </div>
                             
                             <div className="flex justify-center gap-6 pt-4 border-t border-gray-200">
                                {formData.researchLinks.filter(l => !l.hidden).map((link, idx) => (
                                   <div key={idx} className="flex flex-col items-center gap-2 group cursor-pointer">
                                      <div className="w-12 h-12 rounded-full border border-gray-300 p-0.5 grayscale group-hover:grayscale-0 transition-all bg-white">
                                         {link.iconUrl && <img src={link.iconUrl} alt="" className="w-full h-full object-cover rounded-full" />}
                                      </div>
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 group-hover:text-black">{link.name}</span>
                                   </div>
                                ))}
                             </div>
                          </div>

                          {/* Text Column */}
                          <div className="relative">
                             <p className="text-gray-800 text-lg leading-relaxed font-serif first-letter:text-5xl first-letter:font-bold first-letter:mr-3 first-letter:float-left first-letter:text-[#4C1D95]" style={{ fontFamily: formData.bioTextFont }}>
                                {formData.bioText || "Your biography text will appear here. It will be styled with a classic vintage editorial look, featuring a large drop cap and elegant serif typography to tell your story with sophistication."}
                             </p>
                          </div>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
}
